import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdminService } from "services/AdminService";
import {
  emptyStoreDeliveryService,
  StoreDeliveryService,
  StoreServiceTerritoryArea,
  STORE_SERVICE_TEMPLATES,
} from "models/StoreDeliveryService";
import {
  SERVICE_TERRITORIES,
  ServiceTerritory,
} from "admin/manageOrders/manageService/territories";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const FSA_PATTERN = /^[A-Z]\d[A-Z]$/;
const emptyTerritoryForm = {
  name: "",
  fsaCodes: "",
  surchargeCad: "0",
};

const areaKeyFromName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const CATALOG_AREA_KEYS = new Set(
  SERVICE_TERRITORIES.map(area => areaKeyFromName(area.name))
);

const parseFsaCodes = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map(code => code.replace(/\s+/g, "").toUpperCase())
        .filter(Boolean)
    )
  );

const customAreasFromService = (
  areas: StoreServiceTerritoryArea[]
): ServiceTerritory[] =>
  areas
    .filter(area => !CATALOG_AREA_KEYS.has(area.areaKey))
    .map(area => ({ name: area.label, prefixes: area.fsaCodes }));

type WizardStep = "details" | "pricing" | "territory" | "review";

const steps: Array<{ id: WizardStep; label: string }> = [
  { id: "details", label: "Service Details" },
  { id: "pricing", label: "Pricing" },
  { id: "territory", label: "Territory" },
  { id: "review", label: "Review & Activate" },
];

const isWizardStep = (value?: string): value is WizardStep =>
  steps.some(step => step.id === value);

const getServiceId = (service: StoreDeliveryService): string =>
  String(service.id || service._id || "");

const WINDOW_END_ERROR = "Delivery window end must be after the start time.";

const isClockTime = (value?: string): boolean =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "");

const clockToPickerDate = (value?: string): Date | null => {
  if (!isClockTime(value)) return null;
  const [hours, minutes] = String(value).split(":").map(Number);
  return new Date(2000, 0, 1, hours, minutes, 0, 0);
};

const pickerDateToClock = (value: Date | null): string => {
  if (!value) return "";
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
};

const clockAfter = (value?: string): Date => {
  const start = clockToPickerDate(value) || new Date(2000, 0, 1, 0, 0, 0, 0);
  return new Date(start.getTime() + 60 * 1000);
};

const formatClockTime = (value?: string): string => {
  if (!isClockTime(value)) return value || "";
  const [hours, minutes] = String(value).split(":");
  return `${hours}:${minutes}`;
};

const withTemplate = (code: string | null): StoreDeliveryService => {
  const service = emptyStoreDeliveryService();
  const template = STORE_SERVICE_TEMPLATES.find(item => item.code === code);
  return template
    ? {
        ...service,
        ...template,
        masterServiceCode: template.code,
      }
    : service;
};

const money = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value || 0);

const StoreServiceWizard: React.FC = () => {
  const { integrationId, serviceId, step } = useParams<{
    integrationId: string;
    serviceId: string;
    step?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeStep: WizardStep = isWizardStep(step) ? step : "details";
  const isNew = serviceId === "new";
  const [service, setService] = useState<StoreDeliveryService>(() =>
    withTemplate(searchParams.get("template"))
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [territorySearch, setTerritorySearch] = useState("");
  const [territoryPage, setTerritoryPage] = useState(1);
  const [customTerritories, setCustomTerritories] = useState<ServiceTerritory[]>(
    []
  );
  const [addTerritoryOpen, setAddTerritoryOpen] = useState(false);
  const [territoryForm, setTerritoryForm] = useState(emptyTerritoryForm);
  const [territoryFormError, setTerritoryFormError] = useState("");
  const territoryPageSize = 9;

  useEffect(() => {
    document.title = `${isNew ? "Create" : "Edit"} Service | Zulu Courier`;
  }, [isNew]);

  useEffect(() => {
    if (!integrationId || isNew || !serviceId) return;
    setLoading(true);
    AdminService.getStoreDeliveryService(integrationId, serviceId)
      .then(response => {
        if (!response.success || !response.data) {
          throw new Error(response.message || "Unable to load service.");
        }
        setService(response.data);
        setCustomTerritories(customAreasFromService(response.data.territoryAreas || []));
      })
      .catch((error: any) => {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load service."
        );
      })
      .finally(() => setLoading(false));
  }, [integrationId, isNew, serviceId]);

  const selectedArea = (areaKey: string) =>
    service.territoryAreas.find(area => area.areaKey === areaKey);

  const availableTerritories = useMemo(
    () => [...SERVICE_TERRITORIES, ...customTerritories],
    [customTerritories]
  );

  const filteredTerritories = useMemo(() => {
    const search = territorySearch.trim().toLowerCase();
    return search
      ? availableTerritories.filter(
          area =>
            area.name.toLowerCase().includes(search) ||
            area.prefixes.join(" ").toLowerCase().includes(search)
        )
      : availableTerritories;
  }, [availableTerritories, territorySearch]);

  const visibleTerritories = filteredTerritories.slice(
    (territoryPage - 1) * territoryPageSize,
    territoryPage * territoryPageSize
  );
  const territoryPages = Math.max(
    1,
    Math.ceil(filteredTerritories.length / territoryPageSize)
  );

  const setPricing = (
    key: keyof StoreDeliveryService["pricing"],
    value: any
  ) => {
    setService(current => ({
      ...current,
      pricing: { ...current.pricing, [key]: value },
    }));
  };

  const closeTerritoryModal = () => {
    setAddTerritoryOpen(false);
    setTerritoryForm(emptyTerritoryForm);
    setTerritoryFormError("");
  };

  const addNewTerritory = () => {
    const name = territoryForm.name.trim();
    const prefixes = parseFsaCodes(territoryForm.fsaCodes);
    const surchargeCad = Number(territoryForm.surchargeCad);
    if (!name) {
      setTerritoryFormError("Area / city name is required.");
      return;
    }
    if (!prefixes.length) {
      setTerritoryFormError("Enter at least one 3-character postal code.");
      return;
    }
    if (prefixes.some(code => !FSA_PATTERN.test(code))) {
      setTerritoryFormError(
        "Postal codes must be valid 3-character FSA prefixes, such as L8B."
      );
      return;
    }
    if (!Number.isFinite(surchargeCad) || surchargeCad < 0) {
      setTerritoryFormError("Surcharge must be a non-negative number.");
      return;
    }
    const areaKey = areaKeyFromName(name);
    if (availableTerritories.some(area => areaKeyFromName(area.name) === areaKey)) {
      setTerritoryFormError("A territory with this name already exists.");
      return;
    }

    setCustomTerritories(current => [...current, { name, prefixes }]);
    setTerritorySearch("");
    setTerritoryPage(
      Math.ceil((availableTerritories.length + 1) / territoryPageSize)
    );
    setService(current => ({
      ...current,
      territoryAreas: [
        ...current.territoryAreas,
        {
          areaKey,
          label: name,
          fsaCodes: prefixes,
          surchargeCad,
        },
      ],
    }));
    closeTerritoryModal();
  };

  const toggleTerritory = (name: string, prefixes: string[]) => {
    const areaKey = areaKeyFromName(name);
    setService(current => {
      const exists = current.territoryAreas.some(
        area => area.areaKey === areaKey
      );
      return {
        ...current,
        territoryAreas: exists
          ? current.territoryAreas.filter(area => area.areaKey !== areaKey)
          : [
              ...current.territoryAreas,
              {
                areaKey,
                label: name,
                fsaCodes: prefixes,
                surchargeCad: 0,
              },
            ],
      };
    });
  };

  const updateSurcharge = (areaKey: string, value: number) => {
    setService(current => ({
      ...current,
      territoryAreas: current.territoryAreas.map(area =>
        area.areaKey === areaKey ? { ...area, surchargeCad: value } : area
      ),
    }));
  };

  const selectAllTerritories = (selected: boolean) => {
    setService(current => ({
      ...current,
      territoryAreas: selected
        ? availableTerritories.map(area => {
            const areaKey = areaKeyFromName(area.name);
            return (
              current.territoryAreas.find(item => item.areaKey === areaKey) || {
                areaKey,
                label: area.name,
                fsaCodes: area.prefixes,
                surchargeCad: 0,
              }
            );
          })
        : [],
    }));
  };

  const validateStep = (targetStep: WizardStep): boolean => {
    if (targetStep === "details") {
      if (service.name.trim().length < 2) {
        toast.error("Enter a service name.");
        return false;
      }
      if (!service.description?.trim()) {
        toast.error("Enter a description.");
        return false;
      }
      if (
        !isClockTime(service.cutoffTime) ||
        !isClockTime(service.deliveryWindowStart) ||
        !isClockTime(service.deliveryWindowEnd)
      ) {
        toast.error("Enter the cutoff and delivery window times.");
        return false;
      }
      if (service.deliveryWindowStart >= service.deliveryWindowEnd) {
        toast.error(WINDOW_END_ERROR);
        return false;
      }
    }
    if (targetStep === "pricing") {
      const numbers = [
        service.pricing.flatPrice,
        service.pricing.includedDistanceKm,
        service.pricing.additionalKmPrice,
        service.pricing.vehicleSurcharges.car,
        service.pricing.vehicleSurcharges.suv,
        service.pricing.vehicleSurcharges.minivan,
        service.pricing.freeWeightUpToLb,
        service.pricing.additionalPricePerLb,
      ];
      if (numbers.some(value => !Number.isFinite(value) || value < 0)) {
        toast.error("All pricing values must be valid non-negative numbers.");
        return false;
      }
    }
    if (targetStep === "territory" && service.territoryAreas.length === 0) {
      toast.error("Select at least one delivery area.");
      return false;
    }
    return true;
  };

  const saveDraft = async (
    nextStep?: WizardStep
  ): Promise<string | undefined> => {
    if (!integrationId) return;
    setSaving(true);
    try {
      const response = isNew
        ? await AdminService.createStoreDeliveryService(integrationId, service)
        : await AdminService.updateStoreDeliveryService(
            integrationId,
            serviceId || "",
            service
          );
      if (!response.success || !response.data) {
        throw new Error(
          response.error || response.message || "Unable to save service."
        );
      }
      const saved = response.data;
      setService(saved);
      const savedId = getServiceId(saved);
      if (nextStep) {
        navigate(
          `/super_admin/stores/${integrationId}/services/${savedId}/${nextStep}`
        );
      }
      return savedId;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to save service."
      );
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    const index = steps.findIndex(item => item.id === activeStep);
    if (!validateStep(activeStep)) return;
    const nextStep = steps[index + 1]?.id;
    if (nextStep) {
      await saveDraft(nextStep);
    }
  };

  const back = () => {
    const index = steps.findIndex(item => item.id === activeStep);
    const previous = steps[index - 1]?.id;
    if (previous && integrationId && serviceId) {
      navigate(
        `/super_admin/stores/${integrationId}/services/${serviceId}/${previous}`
      );
    } else {
      navigate(`/super_admin/stores/${integrationId}/pricing`);
    }
  };

  const activate = async () => {
    if (!validateStep("details") || !validateStep("pricing") || !validateStep("territory")) {
      return;
    }
    const savedId = await saveDraft();
    if (!savedId || !integrationId) return;
    setSaving(true);
    try {
      const response = await AdminService.activateStoreDeliveryService(
        integrationId,
        savedId
      );
      if (!response.success) {
        throw new Error(response.message || "Unable to activate service.");
      }
      toast.success("Service saved and activated.");
      navigate(`/super_admin/stores/${integrationId}/pricing`);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to activate service."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "500px" }}
        >
          <Puff color="#FFD0B8" height={50} width={50} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center mb-3">
          <Button color="light" className="me-3" onClick={back}>
            <i className="bx bx-left-arrow-alt font-size-20" />
          </Button>
          <div>
            <h4 className="mb-1">
              {isNew ? "Create Service" : `Edit Service - ${service.name}`}
            </h4>
            <span className="text-muted">
              Create a delivery service and configure its pricing and territory.
            </span>
          </div>
          {!isNew && (
            <Badge
              color={
                service.status === "active"
                  ? "success"
                  : service.status === "inactive"
                  ? "secondary"
                  : "warning"
              }
              className="ms-auto"
              pill
            >
              {service.status === "active"
                ? "Active"
                : service.status === "inactive"
                ? "Inactive"
                : "Draft"}
            </Badge>
          )}
        </div>

        <Row>
          <Col lg={2}>
            <Card>
              <CardBody className="p-0">
                {steps.map((item, index) => {
                  const currentIndex = steps.findIndex(
                    candidate => candidate.id === activeStep
                  );
                  const completed = index < currentIndex;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`w-100 border-0 bg-white text-start px-3 py-3 d-flex align-items-center ${
                        item.id === activeStep ? "text-primary fw-semibold" : ""
                      }`}
                      onClick={() => {
                        if (!isNew && integrationId && serviceId) {
                          navigate(
                            `/super_admin/stores/${integrationId}/services/${serviceId}/${item.id}`
                          );
                        }
                      }}
                    >
                      <span
                        className={`avatar-xs rounded-circle d-inline-flex align-items-center justify-content-center me-2 ${
                          completed || item.id === activeStep
                            ? "bg-primary text-white"
                            : "bg-light text-muted"
                        }`}
                      >
                        {completed ? <i className="bx bx-check" /> : index + 1}
                      </span>
                      {item.label}
                    </button>
                  );
                })}
              </CardBody>
            </Card>
          </Col>

          <Col lg={10}>
            <Card>
              <CardBody>
                {activeStep === "details" && (
                  <>
                    <h5>Service Details</h5>
                    <p className="text-muted mb-4">
                      Define how this delivery option appears to customers.
                    </p>
                    <FormGroup>
                      <Label for="serviceName">Service Name</Label>
                      <Input
                        id="serviceName"
                        value={service.name}
                        onChange={event =>
                          setService(current => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="serviceDescription">Description</Label>
                      <Input
                        id="serviceDescription"
                        type="textarea"
                        rows={3}
                        value={service.description}
                        onChange={event =>
                          setService(current => ({
                            ...current,
                            description: event.target.value,
                          }))
                        }
                      />
                    </FormGroup>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Cut Off Time</Label>
                          <DatePicker
                            onFocus={event => event.target.blur()}
                            selected={clockToPickerDate(service.cutoffTime)}
                            onChange={value =>
                              setService(current => ({
                                ...current,
                                cutoffTime: pickerDateToClock(value),
                              }))
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="HH:mm"
                            timeCaption="Time"
                            placeholderText="Select time"
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Delivery Window Start</Label>
                          <DatePicker
                            onFocus={event => event.target.blur()}
                            selected={clockToPickerDate(service.deliveryWindowStart)}
                            onChange={value =>
                              setService(current => ({
                                ...current,
                                deliveryWindowStart: pickerDateToClock(value),
                              }))
                            }
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="HH:mm"
                            timeCaption="Time"
                            placeholderText="Select time"
                            className="form-control"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Delivery Window End</Label>
                          <DatePicker
                            onFocus={event => event.target.blur()}
                            selected={clockToPickerDate(service.deliveryWindowEnd)}
                            onChange={value => {
                              const nextEnd = pickerDateToClock(value);
                              if (
                                isClockTime(service.deliveryWindowStart) &&
                                nextEnd <= service.deliveryWindowStart
                              ) {
                                return;
                              }
                              setService(current => ({
                                ...current,
                                deliveryWindowEnd: nextEnd,
                              }));
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="HH:mm"
                            timeCaption="Time"
                            placeholderText="Select time"
                            className="form-control"
                            minTime={
                              isClockTime(service.deliveryWindowStart)
                                ? clockAfter(service.deliveryWindowStart)
                                : undefined
                            }
                            maxTime={
                              isClockTime(service.deliveryWindowStart)
                                ? new Date(2000, 0, 1, 23, 59, 0, 0)
                                : undefined
                            }
                          />
                          {isClockTime(service.deliveryWindowStart) &&
                          isClockTime(service.deliveryWindowEnd) &&
                          service.deliveryWindowStart >=
                            service.deliveryWindowEnd ? (
                            <div className="invalid-feedback d-block">
                              {WINDOW_END_ERROR}
                            </div>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </>
                )}

                {activeStep === "pricing" && (
                  <>
                    <h5>Pricing</h5>
                    <p className="text-muted mb-4">
                      Configure pricing rules for this service.
                    </p>
                    <PricingSection
                      title="Flat Pricing"
                      description="Charge a flat rate for included distance, then additional per km."
                      enabled={service.pricing.flatPricingEnabled}
                      onToggle={value =>
                        setPricing("flatPricingEnabled", value)
                      }
                    >
                      <Row>
                        <PriceInput
                          label="Flat Price (USD)"
                          value={service.pricing.flatPrice}
                          onChange={value => setPricing("flatPrice", value)}
                        />
                        <PriceInput
                          label="Included Distance (KM)"
                          value={service.pricing.includedDistanceKm}
                          onChange={value =>
                            setPricing("includedDistanceKm", value)
                          }
                        />
                        <PriceInput
                          label="Additional KM Price"
                          value={service.pricing.additionalKmPrice}
                          onChange={value =>
                            setPricing("additionalKmPrice", value)
                          }
                        />
                      </Row>
                    </PricingSection>

                    <PricingSection
                      title="Vehicle Charges"
                      description="Apply surcharges based on vehicle and weight."
                      enabled={service.pricing.vehicleChargesEnabled}
                      onToggle={value =>
                        setPricing("vehicleChargesEnabled", value)
                      }
                    >
                      <Row>
                        {(["car", "suv", "minivan"] as const).map(
                          (vehicle, index) => (
                            <PriceInput
                              key={vehicle}
                              label={
                                [
                                  "Car Surcharge (0–149 lb)",
                                  "SUV Surcharge (150–349 lb)",
                                  "Minivan Surcharge (350–499 lb)",
                                ][index]
                              }
                              value={
                                service.pricing.vehicleSurcharges[vehicle]
                              }
                              onChange={value =>
                                setPricing("vehicleSurcharges", {
                                  ...service.pricing.vehicleSurcharges,
                                  [vehicle]: value,
                                })
                              }
                            />
                          )
                        )}
                      </Row>
                    </PricingSection>

                    <PricingSection
                      title="Weight Charges"
                      description="Apply an additional charge based on order weight."
                      enabled={service.pricing.weightChargesEnabled}
                      onToggle={value =>
                        setPricing("weightChargesEnabled", value)
                      }
                    >
                      <Row>
                        <PriceInput
                          width={6}
                          label="Free Weight (lb)"
                          value={service.pricing.freeWeightUpToLb}
                          onChange={value =>
                            setPricing("freeWeightUpToLb", value)
                          }
                        />
                        <PriceInput
                          width={6}
                          label="Additional Price Per lb"
                          value={service.pricing.additionalPricePerLb}
                          onChange={value =>
                            setPricing("additionalPricePerLb", value)
                          }
                        />
                      </Row>
                    </PricingSection>

                    <PricingSection
                      title="Tax"
                      description="Apply the configured HST to this service."
                      enabled={service.pricing.taxEnabled}
                      onToggle={value => setPricing("taxEnabled", value)}
                    />
                  </>
                )}

                {activeStep === "territory" && (
                  <>
                    <div>
                      <h5>Territory</h5>
                      <p className="text-muted">
                        Add delivery territories and applicable surcharges.
                      </p>
                    </div>
                    <div className="d-flex justify-content-between align-items-end mb-2">
                      <FormGroup className="mb-0" style={{ width: 360 }}>
                        <Label>Select Areas</Label>
                        <Input
                          placeholder="Search area or postal code..."
                          value={territorySearch}
                          onChange={event => {
                            setTerritorySearch(event.target.value);
                            setTerritoryPage(1);
                          }}
                        />
                      </FormGroup>
                      <div className="d-flex">
                        <Button
                          color="primary"
                          className="me-2"
                          onClick={() => setAddTerritoryOpen(true)}
                        >
                          <i className="bx bx-plus me-1" />
                          Add New Territory
                        </Button>
                        <Button
                          color="light"
                          className="me-2"
                          disabled={
                            availableTerritories.length === 0 ||
                            service.territoryAreas.length >=
                              availableTerritories.length
                          }
                          onClick={() => selectAllTerritories(true)}
                        >
                          Select All
                        </Button>
                        <Button
                          color="light"
                          disabled={service.territoryAreas.length === 0}
                          onClick={() => selectAllTerritories(false)}
                        >
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive border rounded">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 45 }} />
                            <th>Area / City</th>
                            <th>3-Character Postal Codes (FSA)</th>
                            <th style={{ width: 150 }}>Surcharge (USD)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleTerritories.map(area => {
                            const areaKey = areaKeyFromName(area.name);
                            const selected = selectedArea(areaKey);
                            return (
                              <tr key={areaKey}>
                                <td>
                                  <Input
                                    type="checkbox"
                                    checked={Boolean(selected)}
                                    onChange={() =>
                                      toggleTerritory(area.name, area.prefixes)
                                    }
                                  />
                                </td>
                                <td>{area.name}</td>
                                <td className="font-size-12 text-muted">
                                  {area.prefixes.join(", ")}
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    disabled={!selected}
                                    value={selected?.surchargeCad || 0}
                                    onChange={event =>
                                      updateSurcharge(
                                        areaKey,
                                        Number(event.target.value)
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    <Modal
                      isOpen={addTerritoryOpen}
                      toggle={closeTerritoryModal}
                      centered
                    >
                      <ModalHeader toggle={closeTerritoryModal}>
                        Add New Territory
                      </ModalHeader>
                      <ModalBody>
                        <p className="text-muted">
                          Add an area that is not already in the list. It will
                          be selected for this service.
                        </p>
                        {territoryFormError && (
                          <Alert color="danger">{territoryFormError}</Alert>
                        )}
                        <FormGroup>
                          <Label for="new-territory-name">Area / City</Label>
                          <Input
                            id="new-territory-name"
                            value={territoryForm.name}
                            maxLength={120}
                            placeholder="e.g. Kitchener / Waterloo"
                            onChange={event =>
                              setTerritoryForm(current => ({
                                ...current,
                                name: event.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="new-territory-fsa">
                            3-Character Postal Codes (FSA)
                          </Label>
                          <Input
                            id="new-territory-fsa"
                            type="textarea"
                            rows={3}
                            value={territoryForm.fsaCodes}
                            placeholder="e.g. N2A, N2B, N2C"
                            onChange={event =>
                              setTerritoryForm(current => ({
                                ...current,
                                fsaCodes: event.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                        <FormGroup className="mb-0">
                          <Label for="new-territory-surcharge">
                            Surcharge (USD)
                          </Label>
                          <Input
                            id="new-territory-surcharge"
                            type="number"
                            min={0}
                            step={0.01}
                            value={territoryForm.surchargeCad}
                            onChange={event =>
                              setTerritoryForm(current => ({
                                ...current,
                                surchargeCad: event.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </ModalBody>
                      <ModalFooter>
                        <Button color="light" onClick={closeTerritoryModal}>
                          Cancel
                        </Button>
                        <Button color="primary" onClick={addNewTerritory}>
                          Add Territory
                        </Button>
                      </ModalFooter>
                    </Modal>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span className="text-muted font-size-13">
                        {service.territoryAreas.length} selected · Showing{" "}
                        {visibleTerritories.length} of{" "}
                        {filteredTerritories.length} areas
                      </span>
                      <div>
                        <Button
                          color="light"
                          size="sm"
                          disabled={territoryPage === 1}
                          onClick={() =>
                            setTerritoryPage(page => Math.max(1, page - 1))
                          }
                        >
                          Previous
                        </Button>
                        <span className="mx-2">
                          {territoryPage} / {territoryPages}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={territoryPage === territoryPages}
                          onClick={() =>
                            setTerritoryPage(page =>
                              Math.min(territoryPages, page + 1)
                            )
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {activeStep === "review" && (
                  <ReviewService service={service} />
                )}

                <div className="d-flex justify-content-end border-top pt-3 mt-4">
                  <Button color="light" className="me-2" onClick={back}>
                    Back
                  </Button>
                  {activeStep === "review" ? (
                    <Button
                      color="primary"
                      disabled={saving}
                      onClick={activate}
                    >
                      {saving && (
                        <Puff
                          color="#FFD0B8"
                          height={18}
                          width={18}
                          wrapperClass="me-2"
                        />
                      )}
                      <i className="bx bx-rocket me-1" />
                      Save &amp; Activate
                    </Button>
                  ) : (
                    <Button color="primary" disabled={saving} onClick={next}>
                      {saving && (
                          <Puff
                            color="#FFD0B8"
                            height={18}
                            width={18}
                            wrapperClass="me-2"
                          />
                        )}
                      Next
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

interface PricingSectionProps {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children?: React.ReactNode;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  title,
  description,
  enabled,
  onToggle,
  children,
}) => (
  <div className="border-bottom pb-4 mb-4">
    <div className="d-flex justify-content-between align-items-start">
      <div>
        <h6 className="mb-1">{title}</h6>
        <p className="text-muted">{description}</p>
      </div>
      <div className="form-check form-switch font-size-20">
        <Input
          type="switch"
          checked={enabled}
          onChange={event => onToggle(event.target.checked)}
        />
      </div>
    </div>
    {enabled && children}
  </div>
);

interface PriceInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  width?: number;
}

const priceText = (value: number): string =>
  Number.isFinite(value) ? String(value) : "";

const PriceInput: React.FC<PriceInputProps> = ({
  label,
  value,
  onChange,
  width = 4,
}) => {
  const [text, setText] = useState(() => priceText(value));

  useEffect(() => {
    setText(current => {
      const typed = current.trim() === "" ? 0 : Number(current);
      return typed === value ? current : priceText(value);
    });
  }, [value]);

  return (
    <Col md={width}>
      <FormGroup>
        <Label>{label}</Label>
        <Input
          type="number"
          min={0}
          step={0.01}
          value={text}
          onChange={event => {
            const next = event.target.value.replace(/^0+(?=\d)/, "");
            setText(next);
            const parsed = next.trim() === "" ? 0 : Number(next);
            onChange(Number.isFinite(parsed) ? parsed : 0);
          }}
        />
      </FormGroup>
    </Col>
  );
};

const ReviewService: React.FC<{ service: StoreDeliveryService }> = ({
  service,
}) => {
  const territoryAreas = service.territoryAreas || [];
  return (
    <>
      <h5 className="mb-1">Review &amp; Activate</h5>
      <p className="text-muted mb-4">
        Review service, pricing, and territory settings before activation.
      </p>
      <ReviewCard title="Service Summary" icon="bx-package">
        <Row>
          <Col md={4}>
            <strong>Service Name</strong>
            <div>{service.name}</div>
          </Col>
          <Col md={4}>
            <strong>Cut Off Time</strong>
            <div>{formatClockTime(service.cutoffTime)}</div>
          </Col>
          <Col md={4}>
            <strong>Delivery Window</strong>
            <div>
              {formatClockTime(service.deliveryWindowStart)} to{" "}
              {formatClockTime(service.deliveryWindowEnd)}
            </div>
          </Col>
          <Col xs={12} className="mt-3">
            <strong>Description</strong>
            <div className="text-break" style={{ whiteSpace: "pre-line" }}>
              {service.description?.trim() || "-"}
            </div>
          </Col>
        </Row>
      </ReviewCard>
      <ReviewCard title="Pricing Summary" icon="bx-dollar-circle">
        <Row>
          <Col md={6} xl={3} className="mb-3 mb-xl-0">
            <strong>Flat Price</strong>
            {service.pricing.flatPricingEnabled ? (
              <>
                <div>{money(service.pricing.flatPrice)}</div>
                <span className="text-muted d-block">
                  Includes {service.pricing.includedDistanceKm} KM
                </span>
                <span className="text-muted d-block">
                  {money(service.pricing.additionalKmPrice)} / additional KM
                </span>
              </>
            ) : (
              <div className="text-muted">Disabled</div>
            )}
          </Col>
          <Col md={6} xl={3} className="mb-3 mb-xl-0">
            <strong>Vehicle Charges</strong>
            {service.pricing.vehicleChargesEnabled ? (
              <div>
                Car {money(service.pricing.vehicleSurcharges.car)} · SUV{" "}
                {money(service.pricing.vehicleSurcharges.suv)} · Minivan{" "}
                {money(service.pricing.vehicleSurcharges.minivan)}
              </div>
            ) : (
              <div className="text-muted">Disabled</div>
            )}
          </Col>
          <Col md={6} xl={3}>
            <strong>Weight Charges</strong>
            {service.pricing.weightChargesEnabled ? (
              <div>
                {service.pricing.freeWeightUpToLb} lb free ·{" "}
                {money(service.pricing.additionalPricePerLb)}/lb
              </div>
            ) : (
              <div className="text-muted">Disabled</div>
            )}
          </Col>
          <Col md={6} xl={3}>
            <strong>Tax</strong>
            {service.pricing.taxEnabled ? (
              <div>
                {Number.isFinite(service.pricing.hst)
                  ? `HST ${service.pricing.hst}%`
                  : "On"}
              </div>
            ) : (
              <div className="text-muted">Disabled</div>
            )}
          </Col>
        </Row>
      </ReviewCard>
      <ReviewCard title="Territory Summary" icon="bx-map">
        {territoryAreas.length === 0 ? (
          <div className="text-muted">No areas selected.</div>
        ) : (
          <>
            <p className="text-muted mb-2">
              {territoryAreas.length}{" "}
              {territoryAreas.length === 1 ? "area" : "areas"} selected
            </p>
            <div
              className="table-responsive border rounded"
              style={{ maxHeight: 280 }}
            >
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "38%" }}>Area / City</th>
                    <th>3-Character Postal Codes (FSA)</th>
                  </tr>
                </thead>
                <tbody>
                  {territoryAreas.map(area => (
                    <tr key={area.areaKey || area.label}>
                      <td className="fw-medium">{area.label?.trim() || "-"}</td>
                      <td style={{ whiteSpace: "normal" }}>
                        {(area.fsaCodes || []).filter(Boolean).join(", ") || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </ReviewCard>
      <Alert color="info" className="mb-0">
        Once activated, this service will be available for eligible Shopify
        orders based on the selected territories and pricing rules.
      </Alert>
    </>
  );
};

const ReviewCard: React.FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="border rounded p-3 mb-3">
    <h6 className="mb-3">
      <i className={`bx ${icon} text-primary me-2`} />
      {title}
    </h6>
    {children}
  </div>
);

export default StoreServiceWizard;
